import Axios from "axios";
import { officeExtentions } from "../../../src/taskpane/config/constants";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";
import { dataSourceItem, FilterItem, FilterSection } from "../../../src/taskpane/redux/models/reduxModels";
import authModule from "../../../utilities/AuthModule";
import { logger } from "../../../utilities/logger";
import { tryDecodeUriComponent, tryEncodeUriComponent } from "../../../utilities/EncodeDecode";
import { IAuthAccount } from "../../../src/taskpane/interfaces/IAuthTokenResultDto";
import { IViewsDropdown } from "../../../src/taskpane/interfaces/IViewsDropdown";
import { ClientTypeContext, INERT_CLIENT_TYPES } from "../../../src/taskpane/components/helpers/dataSourceFieldHelper";
import { DatasourceHelper } from "../../../src/taskpane/components/helpers/dataSourceHelper";
// Moved to a dependency-free sibling so the aggregate panel's cross-library fetcher can
// reuse them without pulling this module's axios/auth/datasource chain. Re-exported
// below: these were never public from here, but keeping the names reachable costs
// nothing and spares a future reader the hunt.
import { extractListItems, extractSelectOptions } from "./filterValueParsing";
import { buildDisplayFilterSections, isEmptyOnlyTitle } from "./filterSectionDisplay";
import { isExcludedFilterField } from "./preselectFieldMatching";

export { extractListItems, extractSelectOptions } from "./filterValueParsing";

export const resetFilterValues = (
    filterSections: FilterSection[]
): FilterSection[] => {
    return filterSections?.map(section => {
        return {
            ...section,
            showMoreExpanded: false,
            daysOffset: null,
            items: section.items.map(item => {
                return {
                    ...item,
                    selected: false
                }
            })
        }
    });
}

export const setFilterSectionsFromFields = async (
    isInOneDrive: boolean,
    oneDriveRootDetails: any,
    currentDirectory: IPageSettings,
    userAccount: IAuthAccount,
    filterFieldList: any[],
    hideTitleFieldFilter: boolean,
    serverRelPath: string,
    localizedMeText: string,
    selectedView?: IViewsDropdown,
    datasourceItems?: dataSourceItem[],
    // Pre-select querystring keys: fields a deep link filters on are exempt from
    // display pruning (see buildDisplayFilterSections). Omitted on the manual
    // panel-open path, which keeps its behaviour unchanged.
    keepFieldKeys?: string[],
    // Client-type restriction context. Defaulted so a missed caller stays inert, but every
    // caller MUST pass it explicitly - a filter panel evaluating a different context than the
    // preselect fast path drifts on datasourceMutated and drops sections.
    clientTypes: ClientTypeContext = INERT_CLIENT_TYPES,
): Promise<FilterSection[]> => {
    try {
        const siteUrl = isInOneDrive ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl;
        const config = {
            headers: {
                Authorization: `Bearer ${isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken()}`,
            }
        }

        const filterSections = await Promise.all(
            filterFieldList
                ?.filter(x => !isExcludedFilterField(x, hideTitleFieldFilter))
                ?.map(async field => {
                    try {
                        const data = (await Axios.post(`${siteUrl}/_api/web/lists('${currentDirectory?.sharePointListId}')/RenderListFilterData?&FieldInternalName=${field.RealFieldName}&RootFolder=${tryEncodeUriComponent(tryDecodeUriComponent(serverRelPath))}&ExcludeFieldFilteringHtml=true${selectedView?.Id ? `&ViewId=${selectedView.Id}` : ""}`, {}, config)).data;

                        const section: FilterSection = {
                            filterField: field,
                            items: data.includes('<SELECT') ? extractSelectOptions(data) : extractListItems(data),
                            showMoreExpanded: false,
                            hasSlider: field.Type === 'DateTime' ? true : false,
                            showSlider: field.Type === 'DateTime' ? true : false
                        }

                        let compactItems: FilterItem[] = [];
                        section.items?.forEach(item => {
                            if (item.title !== '(All)' && item.title !== '(Alles)') {
                                if (field.RealFieldName === 'DocIcon') {
                                    if (officeExtentions.word.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'docx')) {
                                            compactItems.push({
                                                title: 'Word',
                                                value: officeExtentions.word.values,
                                                fileType: 'docx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.excel.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'xlsx')) {
                                            compactItems.push({
                                                title: 'Excel',
                                                value: officeExtentions.excel.values,
                                                fileType: 'xlsx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.powerPoint.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'pptx')) {
                                            compactItems.push({
                                                title: 'PowerPoint',
                                                value: officeExtentions.powerPoint.values,
                                                fileType: 'pptx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (officeExtentions.visio.items.includes(item.value)) {
                                        if (!compactItems.some(x => x.fileType === 'vsdx')) {
                                            compactItems.push({
                                                title: 'Visio',
                                                value: officeExtentions.visio.values,
                                                fileType: 'vsdx',
                                                selected: false
                                            })
                                        }
                                    }
                                    else if (!isEmptyOnlyTitle(item.title)) {
                                        compactItems.push({ ...item, fileType: item.value });
                                    }
                                }
                                else if (field.FieldType === "User" || field.FieldType === "UserMulti") {
                                    if (item.title === userAccount.displayName) {
                                        compactItems.push({ ...item, title: `@${localizedMeText}` });
                                    }
                                    else {
                                        compactItems.push({ ...item });
                                    }
                                }
                                else {
                                    compactItems.push({ ...item });
                                }
                            }
                        });

                        // try {
                        //   const dataSourceField = DatasourceHelper.getInputDataSource(datasourceItems, field.DisplayName);
                        //   if (dataSourceField) {
                        //     const dropdownBinder = new dataSourceDropdownBinder(dataSourceService, dataSourceField, field.DisplayName);
                        //     const property = dropdownBinder.getDataSourcePropertyToBindTo();
                        //     if (property) {
                        //       const datasourceData = await dropdownBinder.getDatasourceEntries();
                        //       const datasourceValues = dropdownBinder.bindDataSourceToDropdown(datasourceData, property);

                        //       datasourceValues.forEach(datasourceValue => {
                        //         const datasourceFilterValue: string = (datasourceValue.key as string)?.trim();
                        //         if (datasourceFilterValue) {
                        //           const compactItem = compactItems.find(x => x.value?.trim().toUpperCase() === datasourceFilterValue.toUpperCase());
                        //           if (!compactItem) {
                        //             const newFilterItem: FilterItem = {
                        //               title: datasourceFilterValue,
                        //               value: datasourceFilterValue,
                        //               selected: false
                        //             }

                        //             compactItems.push(newFilterItem);
                        //           }
                        //         }
                        //       });
                        //     }
                        //   }
                        // } catch (error) {
                        //   logger(error, "CMFileFilterPanel.tsx (Add datasource options)");
                        // }

                        let useDefault: boolean = true;
                        if (field.FieldType === "DateTime") {
                            compactItems.sort((a, b) => {
                                try {
                                    // Convert the value of a and b to Date objects
                                    const aDate = new Date(a.value);
                                    const bDate = new Date(b.value);

                                    // Check if both a and b are valid dates
                                    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
                                        // If both are valid dates, compare their timestamps to sort them
                                        return aDate.getTime() - bDate.getTime();
                                    }
                                } catch (error) {
                                    // Ignore
                                }

                                // If either a or b (or both) are not valid dates, don't change their order
                                return 0;
                            });
                            useDefault = false;
                        }
                        else if (field.FieldType === "Number" && (field.RealFieldName === "Boekjaar" || field.DisplayName?.toLowerCase() === "boekjaar")) {
                            compactItems.sort((a, b) => {
                                const aNum = +a.value;
                                const bNum = +b.value;
                                if (isNaN(aNum) && isNaN(bNum)) return 0; // Both are bad, keep original order
                                if (isNaN(aNum)) return 1;                // A is bad, push it to the end
                                if (isNaN(bNum)) return -1;               // B is bad, push it to the end
                                // Both are good, sort in descending order
                                return bNum - aNum;
                            });
                            useDefault = false;
                        }
                        else if (field.FieldType === "Text" && compactItems.length > 0 && Array.isArray(datasourceItems)) {
                            const datasourceField = DatasourceHelper.getInputDataSource(datasourceItems, field.DisplayName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes);
                            if (Array.isArray(datasourceField?.boundFields) && datasourceField.boundFields.length > 0) {
                                const boundedField = datasourceField.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === field.DisplayName.trim().toLowerCase()));
                                if (boundedField.multiSelect) {
                                    compactItems = compactItems.reduce(
                                        (accumulator: FilterItem[], originalItem: FilterItem) => {
                                            if (originalItem.value?.trim()) {
                                                const itemsToAdd: FilterItem[] = [];
                                                const valueParts: string[] = originalItem.value.split(",");
                                                if (valueParts.length > 1) {
                                                    itemsToAdd.push(
                                                        ...originalItem.value
                                                            .split(",")
                                                            .map(v => v.trim())
                                                            .filter(v => v)
                                                            .map(trimmedValue => ({
                                                                ...originalItem,
                                                                title: trimmedValue,
                                                                value: trimmedValue
                                                            }))
                                                    );
                                                }
                                                else {
                                                    itemsToAdd.push(originalItem);
                                                }
                                                return accumulator.concat(itemsToAdd);
                                            }

                                            return accumulator;
                                        },
                                        [] as FilterItem[]
                                    );
                                    section.datasourceMutated = true;
                                    useDefault = false;
                                }
                            }
                        }

                        if (useDefault) {
                            compactItems = compactItems
                                .sort((a, b) => a.title?.toLowerCase() > b.title?.toLowerCase() ? 1 : -1)
                                .map((item) => {
                                    return {
                                        ...item
                                    }
                                });
                        }

                        section.items = compactItems?.filter((obj, index, self) => index === self.findIndex((o) => o.title === obj.title));

                        return section;
                    } catch (error) {
                        logger(error, "FileFilterHelper.ts (filterFields)");
                        return null;
                    }
                }));

        return buildDisplayFilterSections(filterSections as FilterSection[], keepFieldKeys);
    } catch (error) {
        logger(error, "FileFilterHelper.ts (setFilterSectionsFromFields)");
    }

    return [];
}

// Moved verbatim to camlUtils.ts (dependency-free) so pure modules can import them
// without this file's auth/service chain; re-exported here for existing importers.
export { escapeXml, buildLinearRecursiveCAML } from "./camlUtils";